'use client';

import { createContext, useContext, useRef } from 'react';

// Types
import type { StoreApi } from 'zustand';
import { useStore } from 'zustand';

import type { DatepickerGlobalStoreType } from '@/utils/zustand/stores/datepicker-global-store';
// Store
import { createDatepickerGlobalStore } from '@/utils/zustand/stores/datepicker-global-store';

// Utils
import { getDatepickerGlobalLocalStorage } from '@/utils/localStorage/datepickerGlobalLocalStorage';

const DatepickerGlobalStoreContext = createContext(null);

export type DatepickerGlobalStoreProviderProps = {
    children: React.ReactNode;
};

export const DatepickerGlobalStoreProvider = ({ children }: DatepickerGlobalStoreProviderProps) => {
    const storeRef = useRef<StoreApi<DatepickerGlobalStoreType>>(null); // Add null as initial value

    if (!storeRef.current) {
        storeRef.current = createDatepickerGlobalStore(getDatepickerGlobalLocalStorage());
    }

    return (
        <DatepickerGlobalStoreContext.Provider value={storeRef.current}>
            {children}
        </DatepickerGlobalStoreContext.Provider>
    );
};

export const useDatepickerGlobalStore = <T,>(
    selector: (store: DatepickerGlobalStoreType) => T
): T => {
    const datepickerGlobalStoreContext = useContext(DatepickerGlobalStoreContext);

    if (!datepickerGlobalStoreContext) {
        throw new Error(
            `useDatepickerGlobalStore must be use within DatepickerGlobalStoreProvider`
        );
    }

    return useStore(datepickerGlobalStoreContext, selector);
};
